import React, { useEffect, useId, useRef } from 'react';

import { ArrowRight, Clock, Target } from 'lucide-react';

import { JourneyWorldIcon } from './JourneyWorldIcon';

import { IMcsaWorld } from '../../../../types/mcsa.interface';
import { MCSA_JOURNEY_COPY, MCSA_WORLD_HERO_PATH } from '../../../../helpers/mcsa-journey.copy';

interface JourneyWorldIntroProps {
    world: IMcsaWorld;
    worldCount: number;
    onStart: (world: IMcsaWorld) => void;
    onBack: () => void;
}

export const JourneyWorldIntro = (props: JourneyWorldIntroProps) => {
    const { world, worldCount, onStart, onBack } = props;
    const gameCount = world.games.length;
    const titleId = useId();
    const dialogRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        dialogRef.current?.focus();

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onBack();
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onBack]);

    return (
        <div
            ref={dialogRef}
            className={`mcsa_journey_intro mcsa_journey_intro--${world.key}`}
            aria-labelledby={titleId}
            tabIndex={-1}
        >
            <div className='mcsa_journey_intro__card'>
                <div className='mcsa_journey_intro__hero'>
                    <img
                        src={`${MCSA_WORLD_HERO_PATH}/${world.hero_image}`}
                        alt=''
                    />
                    <p className='text-sm text-semibold text-uppercase mcsa_journey_intro__theme'>
                        {MCSA_JOURNEY_COPY.theme_label} {world.sort_order} of {worldCount}
                    </p>
                </div>

                <div className='mcsa_journey_intro__body'>
                    <div className='mcsa_journey_intro__icon'>
                        <JourneyWorldIcon worldKey={world.key} />
                    </div>

                    <h2 id={titleId} className='text-light color--primary mcsa_journey_intro__title'>
                        {world.intro_title}
                    </h2>
                    <p className='color--txt-tertiary mcsa_journey_intro__copy'>{world.intro_body}</p>

                    <div className='mcsa_journey_intro__meta'>
                        <span>
                            <Clock />
                            {world.pacing_label}
                        </span>
                        <span aria-hidden='true'>·</span>
                        <span>{world.question_count} {MCSA_JOURNEY_COPY.check_ins_label}</span>
                        {gameCount > 0 && (
                            <>
                                <span aria-hidden='true'>·</span>
                                <span>
                                    <Target />
                                    {gameCount} {gameCount === 1 ? MCSA_JOURNEY_COPY.focus_game_label : MCSA_JOURNEY_COPY.focus_games_label}
                                </span>
                            </>
                        )}
                    </div>

                    <div className='mcsa_journey_intro__actions'>
                        <button
                            type='button'
                            className='btn btn--lg btn--radius-full btn--primary mcsa_journey_intro__cta'
                            onClick={() => onStart(world)}
                        >
                            <span>{world.start_cta}</span>
                            <ArrowRight />
                        </button>

                        <button type='button' className='mcsa_journey_intro__back' onClick={onBack}>
                            ← {MCSA_JOURNEY_COPY.back_to_map}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
